import {createApi} from '@reduxjs/toolkit/query/react';
import {baseQueryWithAuth} from 'features/baseQueryWithAuth/baseQueryWithAuth';
import {
  IMainCategoryResponse,
  IMainCategoryRequest,
  IMainCategory,
  IMainCategoryChannelsSearch,
  IMainCategoryReorderRequest,
  IMainCategoryChannelsRequest,
  IMainCategoryUpdateRequest,
  IMainCategoryVODCollectionSearch,
} from 'models/mainCategories';
import {getParamsFromModel} from 'features/helpers';
import {IChannel} from 'models/channels';
import {IVodCollection} from 'models/vodCollections';

export const mainCategoriesApi = createApi({
  reducerPath: 'mainCategoriesApi',
  tagTypes: ['MainCategories', 'MainCategoryChannels', 'MainCategoryVodCollection'],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    find: builder.query<IMainCategoryResponse, Partial<IMainCategoryRequest> | void>({
      query: params => {
        const paramsString = getParamsFromModel<Partial<IMainCategoryRequest>>(params || {})
          .map(param => Object.entries(param).map(([key, value]) => `${key}=${value}`))
          .join('&');
        const searchParams = new URLSearchParams(paramsString);
        return {
          url: `main-categories?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: IMainCategoryResponse) => {
        return response.data ? response : {data: [], metadata: {offset: 0, limit: 0, totalCount: 0}};
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({id}) => ({type: 'MainCategories', id} as const)),
              {type: 'MainCategories', id: 'LIST'},
            ]
          : [],
    }),
    lookup: builder.query<IMainCategoryResponse, Partial<IMainCategoryRequest> | void>({
      query: params => {
        const paramsString = getParamsFromModel<Partial<IMainCategoryRequest>>(params || {})
          .map(param => Object.entries(param).map(([key, value]) => `${key}=${value}`))
          .join('&');
        const searchParams = new URLSearchParams(paramsString);
        return {
          url: `main-categories/lookup?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: IMainCategoryResponse) => {
        return response.data ? response : {data: [], metadata: {offset: 0, limit: 0, totalCount: 0}};
      },
      providesTags: result =>
        result?.data
          ? [
              ...result.data.map(({id}) => ({type: 'MainCategories', id} as const)),
              {type: 'MainCategories', id: 'LIST'},
            ]
          : [],
    }),
    findById: builder.query<IMainCategory, string>({
      query: id => ({
        url: `main-categories/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{type: 'MainCategories', id}],
    }),
    findVodCollection: builder.query<IVodCollection[], Partial<IMainCategoryVODCollectionSearch>>({
      query: params => {
        const paramsString = getParamsFromModel<Partial<IMainCategoryVODCollectionSearch>>(params)
          .map(param => Object.entries(param).map(([key, value]) => `${key}=${value}`))
          .join('&');
        const searchParams = new URLSearchParams(paramsString);
        return {
          url: `main-categories/${params.id}/vod?${searchParams}`,
          method: 'GET',
        };
      },
      transformResponse: (response: IVodCollection[]) => {
        return response ? response : [];
      },
      providesTags: result =>
        result
          ? [
              ...result.map(({id}) => ({type: 'MainCategoryVodCollection', id} as const)),
              {type: 'MainCategoryVodCollection', id: 'LIST'},
            ]
          : [],
    }),
    findChannels: builder.query<IChannel[], IMainCategoryChannelsSearch>({
      query: params => {
        const queryParams: Partial<IMainCategoryChannelsSearch> = {...params};
        delete queryParams['id'];

        const paramsString = getParamsFromModel<Partial<IMainCategoryChannelsSearch>>(queryParams)
          .map(param => Object.entries(param).map(([key, value]) => `${key}=${value}`))
          .join('&');
        const searchParams = new URLSearchParams(paramsString);

        return {
          url: `main-categories/${params.id}/channels?${searchParams}`,
          method: 'GET',
        };
      },
      providesTags: ['MainCategoryChannels'],
    }),
    reorder: builder.mutation<{id: string; order: number}[], IMainCategoryReorderRequest>({
      query: body => ({
        url: `main-categories/reorder`,
        method: 'POST',
        body,
      }),
      invalidatesTags: result =>
        result
          ? [...result.map(({id}) => ({type: 'MainCategories', id} as const)), {type: 'MainCategories', id: 'LIST'}]
          : [],
    }),
    delete: builder.mutation<number, string>({
      query: id => ({
        url: `main-categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, id) => (!error ? [{type: 'MainCategories', id}] : []),
    }),
    insert: builder.mutation<IMainCategory, Partial<IMainCategory>>({
      query: mainCategory => ({
        url: `main-categories`,
        method: 'POST',
        body: mainCategory,
      }),
      invalidatesTags: [{type: 'MainCategories', id: 'LIST'}],
    }),
    update: builder.mutation<Partial<IMainCategory>, IMainCategoryUpdateRequest>({
      query: ({mainCategoryParams, updateFields}) => ({
        url: `main-categories/${mainCategoryParams.id}`,
        method: 'PUT',
        body: {mainCategoryParams, updateFields},
      }),
      invalidatesTags: (_result, error, params) =>
        !error ? [{type: 'MainCategories', id: params.mainCategoryParams.id}] : [],
    }),
    updateChannels: builder.mutation<IChannel[], IMainCategoryChannelsRequest>({
      query: ({channelIds, mainCategoryId, order}) => ({
        url: 'main-categories/channels/bulk-update',
        method: 'POST',
        body: {
          channelIds,
          mainCategoryId,
          order,
        },
      }),
      invalidatesTags: (_result, error, params) =>
        !error ? ['MainCategoryChannels', {type: 'MainCategories', id: params.mainCategoryId}] : [],
    }),
    bulkReorder: builder.mutation<IVodCollection, {id: string; vodIds: string[]}>({
      query: ({id, vodIds}) => ({
        url: `main-categories/${id}/vods/bulk-update`,
        method: 'POST',
        body: {
          vodIds,
        },
      }),
      invalidatesTags: ['MainCategories'],
    }),
  }),
});

export const {
  useFindQuery,
  useLazyFindQuery,
  useFindByIdQuery,
  useLookupQuery,
  useLazyLookupQuery,
  useFindChannelsQuery,
  useFindVodCollectionQuery,
  useLazyFindVodCollectionQuery,
  useLazyFindChannelsQuery,
  useDeleteMutation,
  useInsertMutation,
  useUpdateMutation,
  useReorderMutation,
  useUpdateChannelsMutation,
  useBulkReorderMutation,
} = mainCategoriesApi;
